import { useSelector } from "react-redux";

import { inputFields, popover, Buttons, Spacer } from "../../components";

import { UtilityArrays } from "../../config";

const FilterButtons = ({
  handleFilterChange,
  handleRemoveFilter,
  filters,
  budgetMin,
  setBudgetMin,
  budgetMax,
  setBudgetMax,
  handleReset,
}) => {
  const { countries, programTypes, programs, tags } = useSelector(
    (state) => state.others
  );

  const countrylist = countries.map((country) => ({
    label: country.name,
    value: country.id,
  }));
  const programTypeslist = programTypes.map((programType) => ({
    label: programType.programType,
    value: programType.id,
  }));
  const programslist = programs.map((program) => ({
    label: program.programName,
    value: program.id,
  }));
  const tagslist = tags.map((tag) => ({
    label: tag.tagName,
    value: tag.id,
  }));

  const filterButtons = [
    {
      title: "Countries",
      placement: "bottomLeft",
      hoverTitle: "Click to filter universities by country.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={countrylist}
            onChange={(value) => handleFilterChange({ region: value })}
            value={filters.region}
            allowClear={true}
          />
        </div>
      ),
    },
    {
      title: "Area of Study",
      hoverTitle:
        "Click to explore universities by fields such as Arts & Humanities, Engineering & Technology, and more.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={programTypeslist}
            onChange={(value) =>
              handleFilterChange({ areaOfStudy: value, majors: [] })
            }
            value={filters.areaOfStudy}
            allowClear={true}
          />
        </div>
      ),
    },
    ...(filters.areaOfStudy.length > 0
      ? [
          {
            title: "Majors",
            hoverTitle: "Click to explore universities by specific majors.",
            items: (
              <div className="w-[25em]">
                <inputFields.SelectDropdownMultiple
                  items={programslist}
                  onChange={(value) => handleFilterChange({ majors: value })}
                  value={filters.majors}
                  allowClear={true}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Budget",
      hoverTitle:
        "Click to filter universities based on your budget range in USD.",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.budgetValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                setBudgetMin(null);
                setBudgetMax(null);
                handleFilterChange({ budget: item.value });
              }}
              className={`${
                filters.budget === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
          <div className="flex flex-col gap2 mt-2">
            <div>Custom</div>
            <div className="flex flex-col items-center">
              <div className="w-[150px]">
                <inputFields.DefaultInput
                  placeholder="Min"
                  type="number"
                  value={budgetMin ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBudgetMin(value ? value : null);
                  }}
                />
              </div>
              to
              <div className="w-[150px]">
                <inputFields.DefaultInput
                  placeholder="Max"
                  type="number"
                  value={budgetMax ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBudgetMax(value ? value : "");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Scholarships",
      hoverTitle:
        "Click to filter universities offering scholarships or those without available scholarships.",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.scholarshipValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => handleFilterChange({ scholarships: item.value })}
              className={`${
                filters.scholarships === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Exchange Availibility",
      hoverTitle: "Click to filter universities with exchange availibiities.",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.exchangeValues?.map((item, index) => (
            <div
              key={index}
              onClick={() =>
                handleFilterChange({ exchangeAvailibility: item.value })
              }
              className={`${
                filters.exchangeAvailibility === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Halal Food Availibility",
      hoverTitle: "",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.halalFoodValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => handleFilterChange({ halalFood: item.value })}
              className={`${
                filters.halalFood === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Location Type",
      hoverTitle: "",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.locationTypeValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => handleFilterChange({ locationType: item.value })}
              className={`${
                filters.locationType === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "University Tags",
      hoverTitle:
        "Click to filter universities by tags such as Group of 8, Russell Group, Universitas 21, and more.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={tagslist}
            onChange={(value) => handleFilterChange({ tag: value })}
            value={filters.tag}
            allowClear={true}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex gap-3 items-center">
        <div className="flex gap-2">
          {filterButtons?.map((item, index) => (
            <popover.WhitePopover
              content={item.items}
              placement={item?.placement ?? "bottom"}
              key={index}
            >
              <Buttons.DefaultButton size="small" title={item?.hoverTitle}>
                {item?.title}
              </Buttons.DefaultButton>
            </popover.WhitePopover>
          ))}
        </div>
        {Object.values(filters).some(
          (value) => value !== "" && value?.length > 0
        ) && (
          <div className="font-semibold cursor-pointer" onClick={handleReset}>
            Clear Filters
          </div>
        )}
      </div>
      {Object.values(filters).some(
        (value) => value !== "" && value?.length > 0
      ) && (
        <>
          <Spacer height={20} />
          <div>Filters:</div>
          <div className="flex flex-wrap items-center gap-2 py-3">
            {filters.search && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Search: {filters.search}
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("search")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.region.length > 0 &&
              filters.region.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Countries: {countrylist.find((c) => c.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("region", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.areaOfStudy.length > 0 &&
              filters.areaOfStudy.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Area of Study:{" "}
                  {programTypeslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("areaOfStudy", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.majors.length > 0 &&
              filters.majors.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Majors: {programslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("majors", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.budget && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Budget:{" "}
                {UtilityArrays?.budgetValues.find(
                  (b) => b.value === filters.budget
                )?.title ??
                  (() => {
                    const [min, max] = filters.budget.split("-").map(Number);
                    if (!min && max) return `<${max} $`;
                    if (min && !max) return `>${min} $`;
                    return `${min} - ${max} $`;
                  })()}
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("budget")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.scholarships && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Scholarships:{" "}
                {
                  UtilityArrays?.scholarshipValues.find(
                    (s) => s.value === filters.scholarships
                  )?.title
                }
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("scholarships")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.exchangeAvailibility && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Exchange Availibility:{" "}
                {
                  UtilityArrays?.exchangeValues.find(
                    (s) => s.value === filters.exchangeAvailibility
                  )?.title
                }
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("exchangeAvailibility")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.halalFood && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Halal Food Availibility:{" "}
                {
                  UtilityArrays?.halalFoodValues.find(
                    (s) => s.value === filters.halalFood
                  )?.title
                }
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("halalFood")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.locationType && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Location Type:{" "}
                {
                  UtilityArrays?.locationTypeValues.find(
                    (s) => s.value === filters.locationType
                  )?.title
                }
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("locationType")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.tag.length > 0 &&
              filters.tag.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Tags: {tagslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("tag", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
    </>
  );
};

export default FilterButtons;
